
import { useCallback, useEffect, useState } from 'react';
import { useIsMobile } from './use-mobile';

export interface GridItemSpans {
  row: number;
  col: number;
  isHorizontal: boolean;
}

export interface GridItem {
  id: string;
  spans: GridItemSpans;
}

export interface UseBulletinGridLayoutProps {
  bulletins: any[];
  squarePercentage?: number;
  horizontalPercentage?: number;
  largePercentage?: number;
}

export const useBulletinGridLayout = ({
  bulletins,
  squarePercentage = 67, // Default to 67% squares (2/3)
  horizontalPercentage = 16, // Default to 16% horizontal items
  largePercentage = 17, // Default to 17% large items
}: UseBulletinGridLayoutProps) => {
  const isMobile = useIsMobile();
  const [gridItems, setGridItems] = useState<GridItem[]>([]);

  // Validate percentages add up to 100
  useEffect(() => {
    const total = squarePercentage + horizontalPercentage + largePercentage;
    if (Math.abs(total - 100) > 0.01) {
      console.warn(`Layout percentages should add up to 100. Current total: ${total}`);
    }
  }, [squarePercentage, horizontalPercentage, largePercentage]);

  // Get column class based on screen size - simplified and stable
  const getColumnClass = useCallback(() => {
    return isMobile 
      ? "grid-cols-2 gap-2" 
      : "grid-cols-3 md:grid-cols-4 gap-3 md:gap-4";
  }, [isMobile]);

  // Simplified grid spans - remove complex calculations that cause responsive issues
  const getGridItemSpans = useCallback((index: number, bulletinId: string): GridItemSpans => {
    // For mobile, use mostly squares for stability
    if (isMobile) {
      // Every 6th item is horizontal on mobile for variety
      if (index % 6 === 5) {
        return {
          row: 1,
          col: 2,
          isHorizontal: true,
        };
      }
      
      return {
        row: 1,
        col: 1,
        isHorizontal: false,
      };
    }
    
    // Desktop: simple pattern-based layout
    const pattern = index % 12;
    
    // Every 6th item is horizontal
    if (pattern === 5 || pattern === 11) {
      return {
        row: 1,
        col: 2,
        isHorizontal: true,
      };
    }
    
    // Every 8th item is tall
    if (pattern === 7) {
      return {
        row: 2,
        col: 1,
        isHorizontal: false,
      };
    }

    // Default to square
    return {
      row: 1,
      col: 1,
      isHorizontal: false,
    };
  }, [isMobile]);

  // Update grid items when bulletins change
  useEffect(() => {
    const items = bulletins.map((bulletin, index) => {
      const spans = getGridItemSpans(index, bulletin.id);
      return {
        id: bulletin.id,
        spans,
      };
    });
    
    setGridItems(items);
  }, [bulletins, getGridItemSpans]);

  return {
    gridItems,
    getColumnClass,
    isMobile
  };
};
